import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { normalizeCategory } from '../lib/categories';
import { 
  Upload, ClipboardList, ChevronRight, Trash2, Plus, Check, 
  AlertTriangle, Link2, FileSpreadsheet, Search,
  Users, X, Scissors, ArrowRight, ChevronsUpDown, Loader2
} from 'lucide-react';
import { ClientDetailModal } from './ClientDetailModal';
import { OfficeImportWizard } from './OfficeImportWizard';
import { SplitLineDialog } from './SplitLineDialog';

const PRODUCTION_DAYS = ['FRI', 'SAT', 'SUN', 'MON', 'TUES', 'WED', 'THURS'];

const MONTH_MAP = { Jan: 'January', Feb: 'February', Mar: 'March', Apr: 'April', May: 'May', Jun: 'June', Jul: 'July', Aug: 'August', Sep: 'September', Oct: 'October', Nov: 'November', Dec: 'December' };
const formatWeekLabel = (raw) => {
  const cleaned = raw.replace('Week_', '').replace(/_/g, ' ').trim();
  const parts = cleaned.split(' ');
  if (parts.length === 3) {
    const day = parseInt(parts[0]);
    const month = MONTH_MAP[parts[1]] || parts[1];
    const year = parts[2];
    const suffix = (day >= 11 && day <= 13) ? 'th' : { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th';
    return `${day}${suffix} ${month} ${year}`;
  }
  return raw;
};



const OrdersPage = () => {
  const { isAdmin, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [officeImportOpen, setOfficeImportOpen] = useState(false);  const [orderDetail, setOrderDetail] = useState(null);
  
  // Import wizard
  const [wizardStep, setWizardStep] = useState(0);
  const [parsedData, setParsedData] = useState(null);
  const [importLines, setImportLines] = useState([]);
  const [importClients, setImportClients] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Add Order dialog
  const [addOrderOpen, setAddOrderOpen] = useState(false);
  const [addOrderClientId, setAddOrderClientId] = useState(''); // existing client ID or 'new'
  const [addOrderNewClient, setAddOrderNewClient] = useState({ name: '', key: '', size: 'Large', pack_type: 'Individual' });
  const [addOrderLines, setAddOrderLines] = useState([{ _uid: crypto.randomUUID(), recipe_id: '', recipe_name: '', quantity: 1, allergy_notes: '', production_day: 'UNASSIGNED', meal_size: '' }]);
  const [recipeSearchOpen, setRecipeSearchOpen] = useState(null); // index of open recipe search
  const [clientSearchOpen, setClientSearchOpen] = useState(false);
  
  // Split dialog
  const [splitLine, setSplitLine] = useState(null);
  
  // Client detail dialog
  const [clientDetailOpen, setClientDetailOpen] = useState(false);
  const [clientDetailData, setClientDetailData] = useState(null);
  const [clientAllergyEdits, setClientAllergyEdits] = useState({}); // { lineId: allergyNotes }
  const [savingAllergy, setSavingAllergy] = useState(false);
  
  // Batch assign
  const [selectedLineIds, setSelectedLineIds] = useState(new Set());
  const [batchDay, setBatchDay] = useState('');
  
  const [allRecipes, setAllRecipes] = useState([]);
  const [clients, setClients] = useState([]);
  // Locations
  const [locations, setLocations] = useState([]);
  const [importLocationId, setImportLocationId] = useState('');
  const [filterLocationId, setFilterLocationId] = useState(''); // '' = All Locations (admin only)

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/weekly-orders`);
      setOrders(res.data);
    } catch (e) { toast.error('Failed to fetch orders'); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrderDetail = async (orderId) => {
    try {
      const res = await axios.get(`${API}/weekly-orders/${orderId}`);
      setOrderDetail(res.data);
      setSelectedLineIds(new Set());
    } catch (e) { toast.error('Failed to fetch order details'); }
  };

  useEffect(() => {
    fetchOrders();
    axios.get(`${API}/recipes`).then(r => setAllRecipes(r.data)).catch(() => {});
    axios.get(`${API}/clients`).then(r => setClients(r.data)).catch(() => {});
    axios.get(`${API}/locations`).then(r => setLocations(r.data)).catch(() => {});
  }, [fetchOrders]);

  // Default the admin's Orders filter to their own location (Q3=b)
  useEffect(() => {
    if (isAdmin && user?.location_id && !filterLocationId) {
      setFilterLocationId(user.location_id);
    }
  }, [isAdmin, user, filterLocationId]);

  // ===== IMPORT WIZARD =====
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/orders/parse-excel`, formData);
      setParsedData(res.data);
      setImportLines(res.data.order_lines);
      setImportClients(res.data.clients);
      setWizardStep(1);
      toast.success(`Parsed ${res.data.total_dishes} dishes from ${res.data.week_label}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse file');
    } finally { setUploading(false); }
  };

  const updateImportLine = (index, field, value) => {
    const updated = [...importLines];
    updated[index] = { ...updated[index], [field]: value };
    setImportLines(updated);
  };

  const confirmImport = async () => {
    if (!importLocationId) {
      toast.error('Please choose a location for this import before confirming');
      return;
    }
    try {
      setUploading(true);
      const allergyNotes = {};
      importClients.forEach(c => {
        if (c.allergy_notes && c.allergy_notes.length > 0) {
          allergyNotes[c.name] = c.allergy_notes.join('; ');
        }
      });
      const res = await axios.post(`${API}/orders/confirm-import`, {
        week_label: parsedData.week_label,
        clients: importClients,
        order_lines: importLines,
        allergy_notes: allergyNotes,
        location_id: importLocationId,
      });
      toast.success(`Order created: ${res.data.lines_created} items for ${res.data.clients_count} clients`);
      setWizardStep(0);
      setParsedData(null);
      setImportLines([]);
      setImportLocationId('');
      fetchOrders();
      axios.get(`${API}/clients`).then(r => setClients(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save order');
    }
    finally { setUploading(false); }
  };

  // ===== BATCH ASSIGN =====
  const toggleLineSelect = (lineId) => {
    const next = new Set(selectedLineIds);
    if (next.has(lineId)) next.delete(lineId); else next.add(lineId);
    setSelectedLineIds(next);
  };

  const selectAllInTab = (lines) => {
    const allIds = new Set(lines.map(l => l.id));
    const allSelected = lines.every(l => selectedLineIds.has(l.id));
    if (allSelected) {
      setSelectedLineIds(prev => { const n = new Set(prev); lines.forEach(l => n.delete(l.id)); return n; });
    } else {
      setSelectedLineIds(prev => { const n = new Set(prev); lines.forEach(l => n.add(l.id)); return n; });
    }
  };

  const handleBatchAssign = async () => {
    if (!batchDay || selectedLineIds.size === 0) return;
    try {
      await axios.post(`${API}/order-lines/bulk-assign`, {
        line_ids: Array.from(selectedLineIds),
        production_day: batchDay
      });
      toast.success(`${selectedLineIds.size} items assigned to ${batchDay}`);
      setSelectedLineIds(new Set());
      setBatchDay('');
      if (selectedOrder) fetchOrderDetail(selectedOrder);
    } catch (e) { toast.error('Failed to assign'); }
  };

  const handleBatchDelete = async () => {
    if (selectedLineIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedLineIds.size} selected item(s)?`)) return;
    try {
      await Promise.all(Array.from(selectedLineIds).map(id => axios.delete(`${API}/order-lines/${id}`)));
      toast.success(`${selectedLineIds.size} items deleted`);
      setSelectedLineIds(new Set());
      if (selectedOrder) fetchOrderDetail(selectedOrder);
      fetchOrders();
    } catch (e) { toast.error('Failed to delete'); }
  };

  // ===== RECIPE LINKING (on dashboard) =====
  const linkRecipe = async (lineId, recipeId) => {
    const recipe = allRecipes.find(r => r.id === recipeId);
    try {
      // Also backfill category from the linked recipe so office unlinked lines
      // (which land with empty category) group correctly on the Production view.
      // Safe for school lines too — PUT /order-lines only overrides fields passed.
      const payload = {
        recipe_id: recipeId,
        recipe_name: recipe?.name || '',
      };
      if (recipe?.category) payload.category = recipe.category;
      await axios.put(`${API}/order-lines/${lineId}`, payload);
      // Save mapping for future imports
      const line = orderDetail?.lines?.find(l => l.id === lineId);
      if (line?.dish_name && recipeId) {
        await axios.post(`${API}/recipe-mappings`, {
          import_name: line.dish_name,
          recipe_id: recipeId,
          recipe_name: recipe?.name || ''
        }).catch(() => {});
      }
      toast.success('Recipe linked');
      if (selectedOrder) fetchOrderDetail(selectedOrder);
    } catch (e) { toast.error('Failed to link recipe'); }
  };

  // ===== ADD ORDER =====
  const handleAddOrder = async () => {
    if (!selectedOrder) { toast.error('No order selected'); return; }
    
    let clientName = '';
    let clientId = '';
    
    if (addOrderClientId === 'new') {
      if (!addOrderNewClient.name) { toast.error('Please enter client name'); return; }
      clientName = addOrderNewClient.name;
      // Create new client
      try {
        const res = await axios.post(`${API}/clients`, {
          name: addOrderNewClient.name,
          key: addOrderNewClient.key,
          size: addOrderNewClient.size,
          pack_type: addOrderNewClient.pack_type,
          client_type: 'B2C'
        });
        clientId = res.data.id;
        setClients(prev => [...prev, res.data]);
      } catch (e) { toast.error('Failed to create client'); return; }
    } else {
      const existing = (orderDetail?.clients || []).find(c => c.id === addOrderClientId) || clients.find(c => c.id === addOrderClientId);
      if (!existing) { toast.error('Please select a client'); return; }
      clientName = existing.name;
      clientId = existing.id;
    }
    
    const validLines = addOrderLines.filter(l => l.recipe_id && l.quantity > 0);
    if (validLines.length === 0) { toast.error('Add at least one recipe with quantity'); return; }
    
    try {
      await axios.post(`${API}/order-lines/bulk`, {
        order_id: selectedOrder,
        client_name: clientName,
        client_id: clientId,
        lines: validLines.map(l => ({
          recipe_id: l.recipe_id,
          recipe_name: l.recipe_name,
          quantity: parseInt(l.quantity),
          allergy_notes: l.allergy_notes,
          production_day: l.production_day || 'UNASSIGNED',
          meal_size: l.meal_size || ''
        }))
      });
      toast.success(`${validLines.length} item(s) added for ${clientName}`);
      setAddOrderOpen(false);
      setAddOrderClientId('');
      setAddOrderNewClient({ name: '', key: '', size: 'Large', pack_type: 'Individual' });
      setAddOrderLines([{ _uid: crypto.randomUUID(), recipe_id: '', recipe_name: '', quantity: 1, allergy_notes: '', production_day: 'UNASSIGNED', meal_size: '' }]);
      fetchOrderDetail(selectedOrder);
      fetchOrders();
    } catch (e) { toast.error('Failed to add order'); }
  };

  const addOrderLineRow = () => {
    // Default meal_size to selected client's size
    const selectedClient = (orderDetail?.clients || []).find(c => c.id === addOrderClientId);
    const defaultSize = selectedClient?.size || '';
    setAddOrderLines([...addOrderLines, { _uid: crypto.randomUUID(), recipe_id: '', recipe_name: '', quantity: 1, allergy_notes: '', production_day: 'UNASSIGNED', meal_size: defaultSize }]);
  };

  const updateAddOrderLine = (idx, updates) => {
    setAddOrderLines(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], ...updates };
      return updated;
    });
  };

  const removeAddOrderLine = (idx) => {
    if (addOrderLines.length <= 1) return;
    setAddOrderLines(addOrderLines.filter((_, i) => i !== idx));
  };

  // ===== CLIENT DETAIL =====
  const openClientDetail = (client) => {
    if (!orderDetail) return;
    const lines = orderDetail.lines || [];
    const clientLines = [];
    let totalItems = 0;
    lines.forEach(line => {
      let clientQty = 0;
      let clientAllergyNotes = '';
      for (const q of (line.quantities || [])) {
        if (q.client_name === client.name || q.client_id === client.id) {
          clientQty += q.quantity;
          if (q.allergy_notes) clientAllergyNotes = q.allergy_notes;
        }
      }
      if (clientQty > 0) {
        clientLines.push({ ...line, client_quantity: clientQty, client_allergy_notes: clientAllergyNotes });
        totalItems += clientQty;
      }
    });

    // Group by delivery day for display
    const byDelivery = {};
    clientLines.forEach(line => {
      const day = line.delivery_day === 'M' ? 'Monday Delivery' : line.delivery_day === 'W' ? 'Wednesday Delivery' : line.delivery_day || 'Other';
      if (!byDelivery[day]) byDelivery[day] = [];
      byDelivery[day].push(line);
    });

    setClientDetailData({
      client,
      byDelivery,
      totalItems,
      totalDishes: clientLines.length,
      allergyNotes: (orderDetail.allergy_notes || []).filter(n => n.client_name === client.name || n.client_id === client.id),
    });
    // Initialize allergy edits from current data
    const edits = {};
    clientLines.forEach(line => {
      edits[line.id] = line.client_allergy_notes || '';
    });
    setClientAllergyEdits(edits);
    setClientDetailOpen(true);
  };

  const updateClientLine = async (lineId, field, value) => {
    try {
      await axios.put(`${API}/order-lines/${lineId}`, { [field]: value });
      if (selectedOrder) {
        await fetchOrderDetail(selectedOrder);
        if (clientDetailData?.client) {
          setTimeout(() => openClientDetail(clientDetailData.client), 300);
        }
      }
    } catch (e) { toast.error('Failed to update'); }
  };

  const updateClientQuantity = async (lineId, clientName, quantity) => {
    try {
      const res = await axios.put(`${API}/order-lines/${lineId}/client-quantity`, {
        client_name: clientName,
        client_id: clientDetailData?.client?.id || '',
        quantity: Number(quantity),
      });
      if (res.data?.deleted) {
        toast.success('Line removed (quantity set to 0)');
      } else {
        toast.success('Quantity updated');
      }
      if (selectedOrder) {
        await fetchOrderDetail(selectedOrder);
        if (clientDetailData?.client) {
          setTimeout(() => openClientDetail(clientDetailData.client), 300);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update quantity');
    }
  };

  const saveClientAllergyNotes = async () => {
    if (!clientDetailData?.client) return;
    setSavingAllergy(true);
    try {
      const clientName = clientDetailData.client.name;
      const promises = Object.entries(clientAllergyEdits).map(([lineId, notes]) =>
        axios.put(`${API}/order-lines/${lineId}/client-allergy`, {
          client_name: clientName,
          client_id: clientDetailData.client.id || '',
          allergy_notes: notes,
        })
      );
      await Promise.all(promises);
      toast.success('Allergy notes saved');
      if (selectedOrder) {
        await fetchOrderDetail(selectedOrder);
        if (clientDetailData?.client) {
          setTimeout(() => openClientDetail(clientDetailData.client), 300);
        }
      }
    } catch (e) {
      toast.error('Failed to save allergy notes');
    } finally {
      setSavingAllergy(false);
    }
  };


  const deleteOrder = async (orderId) => {
    if (!window.confirm('Delete this order and all its lines?')) return;
    try {
      await axios.delete(`${API}/weekly-orders/${orderId}`);
      toast.success('Order deleted');
      setSelectedOrder(null);
      setOrderDetail(null);
      fetchOrders();
    } catch (e) { toast.error('Failed to delete'); }
  };

  // ===== WIZARD RENDERING =====
  const renderWizardSteps = () => {
    const steps = ['Upload', 'Preview', 'Link Recipes', 'Confirm'];
    return (
      <div className="flex items-center gap-2 mb-6" data-testid="wizard-steps">
        {steps.map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <div className={`step-dot ${wizardStep > i ? 'completed' : wizardStep === i ? 'active' : 'pending'}`}
              onClick={() => { if (wizardStep > i && i > 0) setWizardStep(i); }}
              style={{ cursor: wizardStep > i ? 'pointer' : 'default' }}>
              {wizardStep > i ? <Check className="w-4 h-4" /> : i + 1}
            </div>
            <span className={`text-xs font-medium ${wizardStep >= i ? 'text-gray-900' : 'text-gray-400'}`}>{label}</span>
            {i < steps.length - 1 && <div className={`step-line ${wizardStep > i ? 'completed' : ''}`} />}
          </div>
        ))}
      </div>
    );
  };

  // STEP 1: Preview
  const renderPreviewStep = () => {
    if (!parsedData) return null;
    const linesWithQty = importLines.filter(l => l.total_quantity > 0);
    
    return (
      <div data-testid="wizard-preview">
        <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
          Order Preview: {formatWeekLabel(parsedData.week_label)}
        </h3>
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Clients ({importClients.length})</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {importClients.map((c, i) => (
              <div key={c.name} className="p-3 bg-white border border-gray-200 rounded-lg" data-testid={`client-card-${i}`}>
                <div className="font-medium text-sm">{c.name}</div>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {c.key && <span className="badge-pill badge-teal font-semibold">{c.key}</span>}
                  <span className="badge-pill badge-teal">{c.size}</span>
                  <span className="badge-pill badge-gray">{c.pack_type}</span>
                </div>
                {c.allergy_notes?.length > 0 && (
                  <div className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {c.allergy_notes.join('; ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Active Orders ({linesWithQty.length} dishes)
          </h4>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden max-h-[350px] overflow-y-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Dish</th>
                  <th>Category</th>
                  <th>Delivery</th>
                  {importClients.map((c) => <th key={c.name}>{c.name.split(' ')[0]}</th>)}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {linesWithQty.map((line) => (
                  <tr key={`${line.dish_name}-${line.delivery_day}-${normalizeCategory(line.category)}`}>
                    <td className="font-medium text-sm">
                      {line.dish_name}
                      {line.is_vegetarian && <span className="ml-1 text-green-600 text-xs">(v)</span>}
                    </td>
                    <td><span className="badge-pill badge-gray text-[10px]">{normalizeCategory(line.category)}</span></td>
                    <td><span className="badge-pill badge-teal text-[10px]">{line.delivery_day}</span></td>
                    {importClients.map((c) => {
                      const q = line.quantities.find(q => q.client_name === c.name);
                      return <td key={c.name} className="tabular-nums text-sm">{q?.quantity || '-'}</td>;
                    })}
                    <td className="font-semibold tabular-nums">{line.total_quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-xs text-gray-400 mb-4">
          {importLines.filter(l => l.total_quantity === 0).length} dishes with zero quantities will be skipped. All items will be imported as UNASSIGNED — assign to production days on the dashboard.
        </p>
        <div className="flex gap-3 justify-end">
          <Button onClick={() => setWizardStep(0)} className="btn-outline">Cancel</Button>
          <Button onClick={() => setWizardStep(2)} className="btn-primary">
            Next: Link Recipes <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  };

  // STEP 2: Recipe Linking
  const renderRecipeLinkStep = () => {
    const linesWithQty = importLines.filter(l => l.total_quantity > 0);
    const unmatched = linesWithQty.filter(l => l.match_type === 'unmatched');
    const matched = linesWithQty.filter(l => l.match_type !== 'unmatched');
    
    return (
      <div data-testid="wizard-recipe-link">
        <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Link Recipes</h3>
        <p className="text-sm text-gray-500 mb-4">
          {matched.length} auto-matched. {unmatched.length} need linking. You can also link recipes later on the dashboard.
        </p>
        {unmatched.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-red-600 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Unmatched ({unmatched.length})
            </h4>
            <div className="space-y-2 max-h-[250px] overflow-y-auto">
              {unmatched.map((line) => {
                const lineIdx = importLines.indexOf(line);
                return (
                  <div key={lineIdx} className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{line.dish_name}</div>
                      <div className="text-xs text-gray-500">{normalizeCategory(line.category)} | Qty: {line.total_quantity}</div>
                    </div>
                    <Select value={line.recipe_id || "none"} onValueChange={(val) => {
                      if (val === "none") {
                        updateImportLine(lineIdx, 'recipe_id', '');
                        updateImportLine(lineIdx, 'recipe_name', '');
                        updateImportLine(lineIdx, 'match_type', 'unmatched');
                      } else {
                        const r = allRecipes.find(r => r.id === val);
                        updateImportLine(lineIdx, 'recipe_id', val);
                        updateImportLine(lineIdx, 'recipe_name', r?.name || '');
                        updateImportLine(lineIdx, 'match_type', 'manual');
                      }
                    }}>
                      <SelectTrigger className="w-[260px] text-sm" data-testid={`link-recipe-${lineIdx}`}>
                        <SelectValue placeholder="Select recipe..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Skip --</SelectItem>
                        {allRecipes.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {matched.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-green-600 mb-3 flex items-center gap-2">
              <Check className="w-4 h-4" /> Auto-Matched ({matched.length})
            </h4>
            <div className="bg-white border border-gray-200 rounded-lg max-h-[150px] overflow-y-auto">
              {matched.map((line) => (
                <div key={line.dish_name} className="flex items-center justify-between px-4 py-2 border-b border-gray-100 last:border-b-0">
                  <span className="text-sm">{line.dish_name}</span>
                  <span className="text-xs text-green-600 flex items-center gap-1"><Link2 className="w-3 h-3" /> {line.recipe_name || line.dish_name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <Button onClick={() => setWizardStep(1)} className="btn-outline">Back</Button>
          <Button onClick={() => setWizardStep(3)} className="btn-primary">
            Next: Confirm <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  };

  // STEP 3: Confirm
  const renderConfirmStep = () => {
    const linesWithQty = importLines.filter(l => l.total_quantity > 0);
    const linked = linesWithQty.filter(l => l.recipe_id);
    const unlinked = linesWithQty.filter(l => !l.recipe_id);
    const totalItems = linesWithQty.reduce((sum, l) => sum + l.total_quantity, 0);
    
    return (
      <div data-testid="wizard-confirm">
        <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Confirm Import</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-white border border-gray-200 rounded-lg text-center">
            <div className="text-xl font-bold tabular-nums">{formatWeekLabel(parsedData?.week_label || '')}</div>
            <div className="text-xs text-gray-500 mt-1">Week</div>
          </div>
          <div className="p-4 bg-white border border-gray-200 rounded-lg text-center">
            <div className="text-2xl font-bold tabular-nums">{importClients.length}</div>
            <div className="text-xs text-gray-500 mt-1">Clients</div>
          </div>
          <div className="p-4 bg-white border border-gray-200 rounded-lg text-center">
            <div className="text-2xl font-bold tabular-nums">{linesWithQty.length}</div>
            <div className="text-xs text-gray-500 mt-1">Dishes</div>
          </div>
          <div className="p-4 bg-white border border-gray-200 rounded-lg text-center">
            <div className="text-2xl font-bold tabular-nums">{totalItems}</div>
            <div className="text-xs text-gray-500 mt-1">Total Items</div>
          </div>
        </div>
        <div className="flex gap-4 mb-6">
          <div className="flex-1 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm font-medium">{linked.length} linked</div>
          {unlinked.length > 0 && <div className="flex-1 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm font-medium">{unlinked.length} unlinked</div>}
          <div className="flex-1 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm font-medium">All items → Unassigned</div>
        </div>

        {/* Location Picker (required) */}
        <div className="mb-6 p-4 bg-emerald-50 border-2 border-emerald-200 rounded-lg">
          <div className="text-xs uppercase tracking-wider font-bold text-emerald-800 mb-2">📍 Location for this import (required)</div>
          <select
            value={importLocationId}
            onChange={(e) => setImportLocationId(e.target.value)}
            className="w-full border border-emerald-300 rounded px-3 py-2 bg-white text-sm"
            data-testid="import-location-select"
          >
            <option value="">-- Choose a location --</option>
            {locations.filter(l => l.active).map(l => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
          <div className="text-xs text-emerald-700 mt-1">This order will be visible only to admins and kitchen staff assigned to the selected location.</div>
        </div>

        <div className="flex gap-3 justify-end">
          <Button onClick={() => setWizardStep(2)} className="btn-outline">Back</Button>
          <Button onClick={confirmImport} className="btn-primary" disabled={uploading || !importLocationId} data-testid="confirm-import-btn">
            {uploading ? 'Saving...' : 'Confirm & Import'}
          </Button>
        </div>
      </div>
    );
  };

  // ===== ORDER DETAIL =====
  const renderOrderDetail = () => {
    if (!orderDetail) return null;
    const lines = orderDetail.lines || [];
    const detailClients = orderDetail.clients || [];
    const allergyNotes = orderDetail.allergy_notes || [];
    
    const grouped = { UNASSIGNED: [] };
    PRODUCTION_DAYS.forEach(d => { grouped[d] = []; });
    lines.forEach(l => {
      const day = l.production_day || 'UNASSIGNED';
      if (!grouped[day]) grouped[day] = [];
      grouped[day].push(l);
    });
    
    const tabOrder = ['UNASSIGNED', ...PRODUCTION_DAYS];
    
    return (
      <div data-testid="order-detail">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{formatWeekLabel(orderDetail.week_label)}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className={`badge-pill ${orderDetail.status === 'confirmed' ? 'badge-teal' : 'badge-gray'}`}>{orderDetail.status}</span>
              <span className="text-sm text-gray-500">{lines.length} dishes | {detailClients.length} clients</span>
            </div>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <Button onClick={() => { setAddOrderOpen(true); }} className="btn-primary text-sm" data-testid="add-order-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Order
              </Button>
              <Button onClick={() => setOfficeImportOpen(true)} variant="outline" className="text-sm" style={{ borderRadius: '9999px' }} data-testid="office-import-btn">
                <FileSpreadsheet className="w-4 h-4 mr-1" /> Add Office Import
              </Button>
              <Button onClick={() => deleteOrder(orderDetail.id)} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 text-sm" style={{ borderRadius: '9999px' }} data-testid="delete-order-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Clients */}
        <div className="flex gap-2 flex-wrap mb-3">
          {detailClients.map(c => (
            <button key={c.id} onClick={() => openClientDetail(c)}
              className="px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs flex items-center gap-1.5 hover:border-[var(--color-primary)] hover:shadow-sm transition-all cursor-pointer"
              data-testid={`client-badge-${c.id}`}>
              <Users className="w-3 h-3 text-gray-400" />
              <span className="font-medium">{c.name}</span>
              {c.key && <><span className="text-gray-400">|</span><span className="font-semibold text-[var(--color-primary-dark)]">{c.key}</span></>}
              <span className="text-gray-400">|</span><span>{c.size}</span>
              <span className="text-gray-400">|</span><span>{c.pack_type}</span>
            </button>
          ))}
        </div>

        {allergyNotes.length > 0 && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <h4 className="text-sm font-semibold text-red-700 mb-1 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Allergy & Dietary Notes</h4>
            {allergyNotes.map((n) => <div key={n.client_name} className="text-xs text-red-600"><span className="font-medium">{n.client_name}:</span> {n.notes}</div>)}
          </div>
        )}

        {/* Batch assign bar */}
        {isAdmin && selectedLineIds.size > 0 && (
          <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3" data-testid="batch-assign-bar">
            <span className="text-sm font-medium text-blue-700">{selectedLineIds.size} selected</span>
            <ArrowRight className="w-4 h-4 text-blue-400" />
            <Select value={batchDay} onValueChange={setBatchDay}>
              <SelectTrigger className="w-[140px] text-sm h-8"><SelectValue placeholder="Assign to..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="UNASSIGNED">Unassigned</SelectItem>
                {PRODUCTION_DAYS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={handleBatchAssign} className="btn-primary h-8 text-xs" disabled={!batchDay}>Assign</Button>
            <div className="border-l border-blue-200 h-5 mx-1"></div>
            <Button onClick={handleBatchDelete} variant="outline" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" style={{ borderRadius: '9999px' }} data-testid="batch-delete-btn">
              <Trash2 className="w-3 h-3 mr-1" /> Delete
            </Button>
            <Button onClick={() => setSelectedLineIds(new Set())} variant="ghost" className="h-8 text-xs text-gray-500">Clear</Button>
          </div>
        )}

        {/* Production Day Tabs */}
        <Tabs defaultValue="UNASSIGNED">
          <TabsList className="mb-3 flex-wrap" style={{ borderRadius: '8px' }}>
            {tabOrder.map(day => {
              const count = grouped[day]?.length || 0;
              return (
                <TabsTrigger key={day} value={day} style={{ borderRadius: '6px' }} className="text-xs"
                  data-testid={`tab-${day}`}>
                  {day === 'UNASSIGNED' ? 'Unassigned' : day} ({count})
                </TabsTrigger>
              );
            })}
          </TabsList>

          {tabOrder.map(day => (
            <TabsContent key={day} value={day}>
              {renderDayTable(grouped[day] || [], day)}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    );
  };

  const renderDayTable = (dayLines, day) => {
    const allSelected = dayLines.length > 0 && dayLines.every(l => selectedLineIds.has(l.id));
    
    return (
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              {isAdmin && (
                <th style={{ width: '40px' }}>
                  <Checkbox checked={allSelected} onCheckedChange={() => selectAllInTab(dayLines)} data-testid={`select-all-${day}`} />
                </th>
              )}
              <th>Dish</th>
              <th>Category</th>
              <th>Delivery</th>
              <th>Recipe Link</th>
              <th>Allergy Notes</th>
              <th>Qty</th>
              {isAdmin && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {dayLines.map(line => (
              <tr key={line.id} className={line.source === 'manual' || line.source?.includes('split') ? 'bg-blue-50/50' : ''}>
                {isAdmin && (
                  <td><Checkbox checked={selectedLineIds.has(line.id)} onCheckedChange={() => toggleLineSelect(line.id)} /></td>
                )}
                <td className="font-medium text-sm">
                  {line.dish_name}
                  {line.meal_size && <span className="ml-1 badge-pill badge-gray text-[9px]">{line.meal_size}</span>}
                  {line.is_vegetarian && <span className="ml-1 text-green-600 text-xs">(v)</span>}
                  {line.source === 'manual' && <span className="ml-1 badge-pill bg-blue-100 text-blue-700 text-[9px]">manual</span>}
                  {line.source?.includes('split') && <span className="ml-1 badge-pill bg-purple-100 text-purple-700 text-[9px]">split</span>}
                </td>
                <td><span className="badge-pill badge-gray text-[10px]">{normalizeCategory(line.category)}</span></td>
                <td><span className="badge-pill badge-teal text-[10px]">{line.delivery_day === 'M' ? 'Mon' : line.delivery_day === 'W' ? 'Wed' : line.delivery_day || '-'}</span></td>
                <td>
                  {isAdmin && (day === 'UNASSIGNED' || !line.recipe_id) ? (
                    <Select value={line.recipe_id || "none"} onValueChange={(val) => {
                      if (val !== "none") linkRecipe(line.id, val);
                    }}>
                      <SelectTrigger className="w-[200px] text-xs h-7" data-testid={`link-${line.id}`}>
                        <SelectValue placeholder="Link recipe..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Not linked --</SelectItem>
                        {allRecipes.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : line.recipe_id ? (
                    <span className="text-xs text-green-600 flex items-center gap-1"><Link2 className="w-3 h-3" />{line.recipe_name}</span>
                  ) : <span className="text-xs text-amber-500">Not linked</span>}
                </td>
                <td>
                  {line.allergy_notes ? (
                    <span className="text-xs text-red-600">{line.allergy_notes}</span>
                  ) : (
                    <span className="text-xs text-gray-400">Via client</span>
                  )}
                </td>
                <td className="tabular-nums font-semibold">{line.total_quantity}</td>
                {isAdmin && (
                  <td>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setSplitLine(line)} data-testid={`split-btn-${line.id}`}
                        className="p-1 text-purple-500 hover:bg-purple-50 rounded" title="Split">
                        <Scissors className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={async () => {
                        await axios.delete(`${API}/order-lines/${line.id}`);
                        fetchOrderDetail(selectedOrder);
                        toast.success('Removed');
                      }} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {dayLines.length === 0 && (
              <tr><td colSpan={isAdmin ? 8 : 6} className="text-center text-gray-400 py-8">
                {day === 'UNASSIGNED' ? 'No unassigned items' : `No items assigned to ${day}`}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // ===== MAIN RENDER =====
  return (
    <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8" data-testid="orders-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-bold text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>Weekly Orders</h1>
          <p className="text-sm text-gray-500 mt-1">Import order sheets, assign production days, and manage last-minute orders</p>
        </div>
        {isAdmin && wizardStep === 0 && (
          <label className="btn-primary px-5 py-2.5 text-sm flex items-center gap-2 cursor-pointer" style={{ borderRadius: '9999px' }}>
            <Upload className="w-4 h-4" /> Import Order Sheet
            <input type="file" accept=".xlsx,.xls" onChange={handleFileUpload} className="hidden" data-testid="order-file-input" />
          </label>
        )}
      </div>

      {loading && <div className="progress-bar mb-4"><div className="progress-bar-fill"></div></div>}

      {/* Import Wizard */}
      {wizardStep > 0 && (
        <div className="card p-6 mb-6" style={{ borderRadius: '12px' }}>
          {renderWizardSteps()}
          {wizardStep === 1 && renderPreviewStep()}
          {wizardStep === 2 && renderRecipeLinkStep()}
          {wizardStep === 3 && renderConfirmStep()}
        </div>
      )}

      {/* Orders List + Detail */}
      {wizardStep === 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-1">
            <div className="card" style={{ borderRadius: '12px' }}>
              <div className="p-4 border-b border-gray-200">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Orders ({(isAdmin ? orders.filter(o => !filterLocationId || (o.location_id || 'main-kitchen') === filterLocationId) : orders).length})</span>
                {isAdmin && locations.length > 1 && (
                  <select
                    value={filterLocationId}
                    onChange={(e) => setFilterLocationId(e.target.value)}
                    className="mt-2 w-full border border-gray-200 rounded px-2 py-1 text-xs"
                    data-testid="orders-location-filter"
                  >
                    <option value="">📍 All Locations</option>
                    {locations.map(l => (
                      <option key={l.id} value={l.id}>📍 {l.name}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="max-h-[600px] overflow-y-auto">
                {orders.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">
                    <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No orders yet.</p>
                  </div>
                ) : (isAdmin ? orders.filter(o => !filterLocationId || (o.location_id || 'main-kitchen') === filterLocationId) : orders).map(order => (
                  <div key={order.id} onClick={() => { setSelectedOrder(order.id); fetchOrderDetail(order.id); }}
                    className={`p-4 border-b border-gray-100 cursor-pointer transition-colors ${selectedOrder === order.id ? 'bg-[var(--color-primary-50)]' : 'hover:bg-gray-50'}`}
                    data-testid={`order-item-${order.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-sm">{formatWeekLabel(order.week_label)}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`badge-pill text-[10px] ${order.status === 'confirmed' ? 'badge-teal' : 'badge-gray'}`}>{order.status}</span>
                          <span className="text-xs text-gray-500">{order.line_count} dishes</span>
                        </div>
                        {isAdmin && (
                          <div className="text-[10px] text-gray-400 mt-1">📍 {locations.find(l => l.id === (order.location_id || 'main-kitchen'))?.name || 'Main Kitchen'}</div>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="card p-6" style={{ borderRadius: '12px', minHeight: '400px' }}>
              {selectedOrder && orderDetail ? renderOrderDetail() : (
                <div className="flex items-center justify-center h-full text-gray-400" style={{ minHeight: '400px' }}>
                  <div className="text-center">
                    <ClipboardList className="w-16 h-16 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">Select an order to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Order Dialog */}
      <Dialog open={addOrderOpen} onOpenChange={setAddOrderOpen}>
        <DialogContent className="sm:max-w-[900px] max-h-[85vh] overflow-y-auto" style={{ borderRadius: '12px' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>Add Order</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-3">
            {/* Client Selection */}
            <div className="grid gap-2">
              <Label className="font-semibold">Client</Label>
              <Popover open={clientSearchOpen} onOpenChange={setClientSearchOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between text-sm font-normal" data-testid="add-order-client-trigger">
                    {addOrderClientId === 'new' ? addOrderNewClient.name || 'New Client...' :
                     addOrderClientId ? clients.find(c => c.id === addOrderClientId)?.name || 'Select client...' :
                     'Search or add client...'}
                    <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" style={{ borderRadius: '12px' }}>
                  <Command>
                    <CommandInput placeholder="Search clients..." />
                    <CommandList>
                      <CommandEmpty>No clients found.</CommandEmpty>
                      <CommandGroup heading="Clients from this order">
                        {(orderDetail?.clients || []).map(c => (
                          <CommandItem key={c.id} value={c.name} onSelect={() => {
                            setAddOrderClientId(c.id);
                            setClientSearchOpen(false);
                            // Set default meal_size on all lines to this client's size
                            setAddOrderLines(prev => prev.map(l => ({ ...l, meal_size: l.meal_size || c.size })));
                          }}>
                            <Check className={`w-4 h-4 mr-2 ${addOrderClientId === c.id ? 'opacity-100' : 'opacity-0'}`} />
                            <span className="font-medium">{c.name}</span>
                            {c.key && <span className="ml-2 badge-pill badge-teal text-[10px]">{c.key}</span>}
                            <span className="ml-auto text-xs text-gray-400">{c.size} | {c.pack_type}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                      <CommandGroup heading="Create New">
                        <CommandItem value="__new_client__" onSelect={() => {
                          setAddOrderClientId('new');
                          setClientSearchOpen(false);
                        }}>
                          <Plus className="w-4 h-4 mr-2" />
                          <span className="font-medium">Add New Client</span>
                        </CommandItem>
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Existing client - read-only info */}
            {addOrderClientId && addOrderClientId !== 'new' && (() => {
              const sel = (orderDetail?.clients || []).find(c => c.id === addOrderClientId) || clients.find(c => c.id === addOrderClientId);
              return sel ? (
                <div className="grid grid-cols-4 gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div><Label className="text-xs text-gray-500">Organisation</Label><p className="text-sm font-medium">{sel.name}</p></div>
                  <div><Label className="text-xs text-gray-500">Key</Label><p className="text-sm font-medium">{sel.key || '-'}</p></div>
                  <div><Label className="text-xs text-gray-500">Size</Label><p className="text-sm font-medium">{sel.size}</p></div>
                  <div><Label className="text-xs text-gray-500">Pack Type</Label><p className="text-sm font-medium">{sel.pack_type}</p></div>
                </div>
              ) : null;
            })()}

            {/* New client fields */}
            {addOrderClientId === 'new' && (
              <div className="grid grid-cols-2 gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="grid gap-1">
                  <Label className="text-xs">Organisation Name</Label>
                  <Input value={addOrderNewClient.name} onChange={(e) => setAddOrderNewClient(p => ({ ...p, name: e.target.value }))}
                    placeholder="Client name" data-testid="new-client-name" />
                </div>
                <div className="grid gap-1">
                  <Label className="text-xs">Key</Label>
                  <Input value={addOrderNewClient.key} onChange={(e) => setAddOrderNewClient(p => ({ ...p, key: e.target.value }))}
                    placeholder="e.g. A, B, C" data-testid="new-client-key" />
                </div>
                <div className="grid gap-1">
                  <Label className="text-xs">Size</Label>
                  <Select value={addOrderNewClient.size} onValueChange={(v) => setAddOrderNewClient(p => ({ ...p, size: v }))}>
                    <SelectTrigger data-testid="new-client-size"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Large">Large</SelectItem>
                      <SelectItem value="Small">Small</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1">
                  <Label className="text-xs">Pack Type</Label>
                  <Select value={addOrderNewClient.pack_type} onValueChange={(v) => setAddOrderNewClient(p => ({ ...p, pack_type: v }))}>
                    <SelectTrigger data-testid="new-client-pack"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Individual">Individual</SelectItem>
                      <SelectItem value="Sharing">Sharing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Order Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Order Items</Label>
                <Button type="button" onClick={addOrderLineRow} variant="outline" className="text-xs h-7" style={{ borderRadius: '9999px' }}>
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-3">
                {addOrderLines.map((line, idx) => (
                  <div key={line._uid} className="p-4 bg-gray-50 border border-gray-200 rounded-lg" data-testid={`add-order-line-${idx}`}>
                    {/* Row 1: Recipe full width */}
                    <div className="mb-3">
                      <Label className="text-xs text-gray-500 mb-1 block">Recipe</Label>
                      <Popover open={recipeSearchOpen === idx} onOpenChange={(open) => setRecipeSearchOpen(open ? idx : null)}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-between text-sm font-normal h-10" data-testid={`add-order-recipe-${idx}`}>
                            <span className="truncate">{line.recipe_name || 'Search recipe...'}</span>
                            <ChevronsUpDown className="w-3 h-3 ml-2 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[450px] p-0" style={{ borderRadius: '12px' }}>
                          <Command>
                            <CommandInput placeholder="Search recipes..." />
                            <CommandList>
                              <CommandEmpty>No recipes found.</CommandEmpty>
                              <CommandGroup>
                                {allRecipes.map(r => (
                                  <CommandItem key={r.id} value={r.name} onSelect={() => {
                                    const sizes = r.size_variations?.map(sv => sv.size_name) || [];
                                    const selectedClient = (orderDetail?.clients || []).find(c => c.id === addOrderClientId);
                                    const defaultSize = selectedClient?.size || '';
                                    const autoSize = sizes.includes(defaultSize) ? defaultSize : (sizes[0] || '');
                                    updateAddOrderLine(idx, { recipe_id: r.id, recipe_name: r.name, meal_size: autoSize });
                                    setRecipeSearchOpen(null);
                                  }}>
                                    <Check className={`w-4 h-4 mr-2 shrink-0 ${line.recipe_id === r.id ? 'opacity-100' : 'opacity-0'}`} />
                                    <span className="truncate">{r.name}</span>
                                    <span className="ml-auto text-xs text-gray-400 shrink-0">{normalizeCategory(r.category)}</span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    {/* Row 2: Qty, Meal Size, Production Day, Allergy, Delete */}
                    <div className="grid grid-cols-12 gap-3 items-end">
                      <div className="col-span-1">
                        <Label className="text-xs text-gray-500">Qty</Label>
                        <Input type="number" min="1" value={line.quantity}
                          onChange={(e) => updateAddOrderLine(idx, { quantity: e.target.value })}
                          className="text-sm h-10" data-testid={`add-order-qty-${idx}`} />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-gray-500">Meal Size</Label>
                        {(() => {
                          const recipe = allRecipes.find(r => r.id === line.recipe_id);
                          const sizes = recipe?.size_variations?.map(sv => sv.size_name) || [];
                          if (sizes.length <= 1) {
                            return <Input value={sizes[0] || 'Standard'} disabled className="text-sm h-10 bg-gray-100" />;
                          }
                          return (
                            <Select value={line.meal_size || sizes[0]} onValueChange={(v) => updateAddOrderLine(idx, { meal_size: v })}>
                              <SelectTrigger className="text-sm h-10" data-testid={`add-order-size-${idx}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {sizes.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          );
                        })()}
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs text-gray-500">Production Day</Label>
                        <Select value={line.production_day} onValueChange={(v) => updateAddOrderLine(idx, { production_day: v })}>
                          <SelectTrigger className="text-sm h-10"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="UNASSIGNED">Unassigned</SelectItem>
                            {PRODUCTION_DAYS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-5">
                        <Label className="text-xs text-gray-500">Allergy/Dietary Notes</Label>
                        <Input value={line.allergy_notes}
                          onChange={(e) => updateAddOrderLine(idx, { allergy_notes: e.target.value })}
                          placeholder="Notes..." className="text-sm h-10" />
                      </div>
                      <div className="col-span-1 flex justify-end pb-1">
                        {addOrderLines.length > 1 && (
                          <button onClick={() => removeAddOrderLine(idx)} className="p-1.5 text-red-500 hover:bg-red-50 rounded">
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setAddOrderOpen(false)} className="btn-outline">Cancel</Button>
            <Button onClick={handleAddOrder} className="btn-primary" data-testid="save-add-order-btn">
              Add Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split Dialog */}
      <SplitLineDialog line={splitLine} order={orderDetail} days={PRODUCTION_DAYS}
        onClose={() => setSplitLine(null)}
        onDone={() => { setSplitLine(null); if (selectedOrder) fetchOrderDetail(selectedOrder); fetchOrders(); }} />

      {/* Client Detail Dialog */}
      <ClientDetailModal
        open={clientDetailOpen}
        onOpenChange={setClientDetailOpen}
        data={clientDetailData}
        isAdmin={isAdmin}
        clientAllergyEdits={clientAllergyEdits}
        setClientAllergyEdits={setClientAllergyEdits}
        updateClientLine={updateClientLine}
        updateClientQuantity={updateClientQuantity}
        saveClientAllergyNotes={saveClientAllergyNotes}
        savingAllergy={savingAllergy}
      />

      {/* Office Import Wizard */}
      <OfficeImportWizard
        open={officeImportOpen}
        onClose={() => setOfficeImportOpen(false)}
        orderId={selectedOrder}
        onImported={() => selectedOrder && fetchOrderDetail(selectedOrder)}
      />
    </div>
  );
};

export default OrdersPage;
